import { useApi } from '@classgo/app-kit';
import { createGroupInputSchema, updateGroupInputSchema } from '@classgo/shared';
import { Input, Select } from '@classgo/ui';

import { academicApi } from '../academic/api';
import type { GradeView, GroupView, LevelView, SchoolYearView } from '../academic/types';
import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { FormShell } from './form-shell';

const JORNADAS = ['unica', 'manana', 'tarde', 'nocturna'] as const;

/**
 * Create or edit a group: year, sede, grade, name and jornada.
 */
export const GroupForm = ({
  years,
  sedes,
  levels,
  grades,
  schoolYearId,
  sedeId,
  current,
  onDone,
}: {
  years: readonly SchoolYearView[];
  sedes: readonly { id: string; name: string }[];
  levels: readonly LevelView[];
  grades: readonly GradeView[];
  schoolYearId: string;
  sedeId: string;
  current: GroupView | null;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const form = useForm({
    schema: current ? updateGroupInputSchema : createGroupInputSchema,
    initial: {
      schoolYearId: current?.schoolYearId ?? schoolYearId,
      sedeId: current?.sedeId ?? sedeId,
      gradeId: current?.gradeId ?? grades[0]?.id ?? '',
      name: current?.name ?? '',
      jornada: current?.jornada ?? ('unica' as GroupView['jornada']),
    },
    toInput: (values) => ({
      ...(current ? {} : { schoolYearId: values.schoolYearId }),
      sedeId: values.sedeId,
      gradeId: values.gradeId,
      name: values.name,
      jornada: values.jornada,
    }),
  });
  const levelName = (grade: GradeView) =>
    levels.find((level) => level.id === grade.levelId)?.name ?? '';
  const error = (key: 'schoolYearId' | 'sedeId' | 'gradeId' | 'name' | 'jornada') =>
    form.fieldError(key) ? { error: form.fieldError(key) } : {};
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            if (current) await academicApi(api).update('groups', current.id, input);
            else await academicApi(api).create('groups', input);
          })
          .then((ok) => ok && onDone());
      }}
    >
      {current ? null : (
        <Select
          label={t('groups.field.year')}
          value={form.values.schoolYearId}
          onChange={(event) => form.set('schoolYearId', event.target.value)}
          options={years.map((year) => ({ value: year.id, label: year.name }))}
          {...error('schoolYearId')}
        />
      )}
      <Select
        label={t('groups.field.sede')}
        value={form.values.sedeId}
        onChange={(event) => form.set('sedeId', event.target.value)}
        options={sedes.map((sede) => ({ value: sede.id, label: sede.name }))}
        {...error('sedeId')}
      />
      <Select
        label={t('groups.field.grade')}
        value={form.values.gradeId}
        onChange={(event) => form.set('gradeId', event.target.value)}
        placeholder={grades.length === 0 ? t('groups.noGrade') : undefined}
        options={grades.map((grade) => ({
          value: grade.id,
          label: `${grade.name} · ${levelName(grade)}`,
        }))}
        {...error('gradeId')}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('groups.field.name')}
          help={t('groups.field.name.help')}
          required
          value={form.values.name}
          onChange={(event) => form.set('name', event.target.value)}
          {...error('name')}
        />
        <Select
          label={t('groups.field.jornada')}
          value={form.values.jornada}
          onChange={(event) => form.set('jornada', event.target.value as GroupView['jornada'])}
          options={JORNADAS.map((jornada) => ({
            value: jornada,
            label: t(`groups.jornada.${jornada}`),
          }))}
          {...error('jornada')}
        />
      </div>
    </FormShell>
  );
};
